import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AgendamentoDto,
  AgendarMensagemEntrada,
  CriarRespostaRapidaEntrada,
  GrupoDaNotificacao,
  Pagina,
  RespostaNotificacoes,
  RespostaRapidaDto,
  ResultadoAgendamento,
  TarefaDto,
  TipoNotificacao,
  TipoTarefa,
  RetornoNoCalendarioDto,
} from '@crm/shared'
import { api } from './cliente'

export const chavesOperacao = {
  notificacoes: ['notificacoes'] as const,
  tarefas: (filtro: Record<string, unknown>) => ['tarefas', filtro] as const,
  respostas: ['respostas-rapidas'] as const,
  agendamentos: (filtro: Record<string, unknown>) => ['agendamentos', filtro] as const,
}

// ─────────────────────── notificações ───────────────────────

/**
 * O sino: primeira página das pendentes, curta.
 *
 * O recorte por tipo vai ao servidor, e não é peneirado na tela. Filtrando as
 * vinte já carregadas, escolher "Tarefa" mostraria só as tarefas que por
 * acaso estivessem entre as vinte mais recentes — quem tem trinta veria duas e
 * concluiria que só existem duas. Com o tipo na consulta, vêm as vinte
 * primeiras **daquele** tipo.
 *
 * `tipo` entra na chave de cache: cada recorte é uma lista própria, e o sino
 * sem filtro (que os avisos de canto também usam) continua intacto.
 */
export function useNotificacoes(tipo: TipoNotificacao | 'todos' = 'todos') {
  return useQuery({
    queryKey: [...chavesOperacao.notificacoes, 'sino', tipo],
    queryFn: () => api<RespostaNotificacoes>('/api/notificacoes', { query: { tamanho: 20, tipo } }),
    // O cronômetro e os lembretes nascem no worker, sem evento para a tela.
    refetchInterval: 60_000,
  })
}

/**
 * Os avisos pendentes de uma marca da borda (ADR 0035). Só busca com a caixa
 * aberta; o contador vem da consulta do sino, que já roda sempre.
 */
export function useNotificacoesDoGrupo(grupo: GrupoDaNotificacao | null) {
  return useQuery({
    queryKey: [...chavesOperacao.notificacoes, 'grupo', grupo],
    queryFn: () =>
      api<RespostaNotificacoes>('/api/notificacoes', {
        query: { tamanho: 30, grupo: grupo ?? '' },
      }),
    enabled: grupo !== null,
  })
}

export interface FiltroDeNotificacoes {
  pagina: number
  tamanho: number
  incluirDispensadas: boolean
  tipo: TipoNotificacao | 'todos'
}

/**
 * A tela cheia. Chave de cache própria, porque convive aberta com o sino: a
 * mesma chave faria uma lista sobrescrever a outra a cada atualização.
 */
export function useTodasNotificacoes(filtro: FiltroDeNotificacoes) {
  return useQuery({
    queryKey: [...chavesOperacao.notificacoes, 'tela', filtro],
    queryFn: () =>
      api<RespostaNotificacoes>('/api/notificacoes', {
        query: {
          pagina: filtro.pagina,
          tamanho: filtro.tamanho,
          tipo: filtro.tipo,
          ...(filtro.incluirDispensadas ? { incluirDispensadas: 'true' } : {}),
        },
      }),
    refetchInterval: 60_000,
  })
}

export function useDispensarNotificacao() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/notificacoes/${id}/dispensar`, { metodo: 'POST' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesOperacao.notificacoes }),
  })
}

export function useDispensarTodas() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api<{ dispensadas: number }>('/api/notificacoes/dispensar-todas', { metodo: 'POST' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesOperacao.notificacoes }),
  })
}

// ─────────────────────── tarefas ───────────────────────

export interface FiltroTarefasTela {
  de?: string
  ate?: string
  apenasMinhas: boolean
  incluirConcluidas: boolean
  contatoId?: string
}

export function useTarefas(filtro: FiltroTarefasTela) {
  return useQuery({
    queryKey: chavesOperacao.tarefas(filtro as unknown as Record<string, unknown>),
    queryFn: () =>
      api<TarefaDto[]>('/api/tarefas', {
        query: {
          de: filtro.de,
          ate: filtro.ate,
          apenasMinhas: String(filtro.apenasMinhas),
          incluirConcluidas: String(filtro.incluirConcluidas),
          contatoId: filtro.contatoId,
        },
      }),
  })
}

function invalidarTarefas(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: ['tarefas'] })
  void cliente.invalidateQueries({ queryKey: chavesOperacao.notificacoes })
}

/**
 * O schema do servidor usa `z.coerce.date()`, então o tipo inferido pede
 * `Date`. A tela trabalha com o texto do input `datetime-local`, e converter
 * duas vezes só para agradar o TypeScript não ajuda ninguém — este é o formato
 * que sai do formulário.
 */
export interface TarefaEntradaTela {
  titulo?: string
  tipo?: TipoTarefa
  descricao?: string | null
  contatoId?: string | null
  usuarioId?: string | null
  dataHora?: string
  lembrete?: boolean
  som?: boolean
  concluida?: boolean
}

export function useCriarTarefa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: TarefaEntradaTela) =>
      api<TarefaDto>('/api/tarefas', { metodo: 'POST', corpo: dados }),
    onSuccess: () => invalidarTarefas(cliente),
  })
}

export function useAtualizarTarefa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: TarefaEntradaTela }) =>
      api<TarefaDto>(`/api/tarefas/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => invalidarTarefas(cliente),
  })
}

export function useExcluirTarefa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/api/tarefas/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => invalidarTarefas(cliente),
  })
}

// ─────────────────── respostas rápidas ───────────────────

export function useRespostasRapidas() {
  return useQuery({
    queryKey: chavesOperacao.respostas,
    queryFn: () => api<RespostaRapidaDto[]>('/api/respostas-rapidas'),
    staleTime: 5 * 60_000,
  })
}

export function useCriarRespostaRapida() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: CriarRespostaRapidaEntrada) =>
      api<RespostaRapidaDto>('/api/respostas-rapidas', { metodo: 'POST', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesOperacao.respostas }),
  })
}

export function useAtualizarRespostaRapida() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Record<string, unknown> }) =>
      api<RespostaRapidaDto>(`/api/respostas-rapidas/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesOperacao.respostas }),
  })
}

export function useExcluirRespostaRapida() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/respostas-rapidas/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesOperacao.respostas }),
  })
}

// ─────────────────── mensagem agendada ───────────────────

export function useAgendamentos(filtro: {
  status?: string
  pagina: number
  tamanho: number
  /** A semana do calendário: as que saem neste intervalo. */
  de?: string
  ate?: string
  apenasMinhas?: 'true' | 'false'
}, habilitado = true) {
  return useQuery({
    queryKey: chavesOperacao.agendamentos(filtro),
    queryFn: () => api<Pagina<AgendamentoDto>>('/api/agendamentos', { query: filtro }),
    // Sem "agendar_mensagem" a rota recusa: o Calendário não pede, em vez de levar 403 a cada abertura.
    enabled: habilitado,
  })
}

/**
 * Os agendamentos de um contato só, para a ficha dele.
 *
 * Traz todos os estados, e não só os pendentes: a ficha é histórico. Saber
 * que a mensagem de retorno já saiu ontem muda o que se escreve hoje tanto
 * quanto saber que ela ainda vai sair.
 */
export function useAgendamentosDoContato(contatoId: string, habilitado = true) {
  const filtro = { contatoId, pagina: 1, tamanho: 50 }
  return useQuery({
    queryKey: chavesOperacao.agendamentos(filtro),
    queryFn: () => api<Pagina<AgendamentoDto>>('/api/agendamentos', { query: filtro }),
    enabled: habilitado,
  })
}

export function useAgendarMensagem() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: AgendarMensagemEntrada) =>
      api<ResultadoAgendamento>('/api/agendamentos', { metodo: 'POST', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['agendamentos'] }),
  })
}

export function useCancelarAgendamento() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/agendamentos/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['agendamentos'] }),
  })
}

/** Os retornos marcados da semana, para o calendário (08/10/2026). */
export function useRetornosDaSemana(
  de: string,
  ate: string,
  apenasMinhas: boolean,
  /** Retorno é de conversa: a rota pede "bate_papo.ver". */
  habilitado = true,
) {
  return useQuery({
    queryKey: ['conversas', 'retornos', de, ate, apenasMinhas],
    queryFn: () =>
      api<RetornoNoCalendarioDto[]>('/api/chamados/retornos', {
        query: { de, ate, apenasMinhas: String(apenasMinhas) },
      }),
    enabled: habilitado,
  })
}

/** "Cancelar" do aviso de cliente que respondeu antes: as programadas da pessoa nesta conversa. */
export function useCancelarAgendadasDaConversa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (chamadoId: string) =>
      api<{ canceladas: number }>('/api/agendamentos/cancelar-da-conversa', {
        metodo: 'POST',
        corpo: { id: chamadoId },
      }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['agendamentos'] }),
  })
}

/** Desfaz o cancelamento dentro da janela de arrependimento. */
export function useRestaurarAgendamento() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/agendamentos/${id}/restaurar`, { metodo: 'POST' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['agendamentos'] }),
  })
}

export function useCancelarLote() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (loteId: string) =>
      api<{ cancelados: number }>(`/api/agendamentos/lote/${loteId}`, { metodo: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['agendamentos'] }),
  })
}

// ─────────────────────── rodízio ───────────────────────
